// ─── ABCP Preparation — config storage ───────────────────────────────────────
// Stores per-year overrides in localStorage. ABCPReport reads these overrides
// at mount / year-change and passes them down as props to each page component.
// When a field is missing from localStorage the page component falls back to
// its original hardcoded value, so the report always renders even with no data.

const STORAGE_KEY = (year) => `abcp-config-${year}`;

// ─── Default values (mirrors the hardcoded content in ABCPReport.js) ──────────
const DEFAULT_FOREWORD_PARAGRAPHS = [
  "Since 2016, the Rwanda Biomedical Centre (RBC), through its Blood Transfusion Division (BTD), has upheld a tradition of strategic foresight by preparing the Annual Blood Collection Plan (ABCP) before the start of each year. This plan has been instrumental in ensuring a reliable supply of safe blood and blood products, while also strengthening donor recruitment and retention nationwide. By anchoring operations in careful planning, the Division has safeguarded both efficiency and public health.",
  "In 2025, BTD management has taken a significant step forward by consolidating all major operational frameworks into a single, comprehensive document: the BTD ANNUAL ACTIVITY BOOK. This innovation integrates the Blood Collection Plan, the Hemovigilance Activity Plan, the Bio-Banking Unit Activity Plan, and the Field Activity Plan. Together, these plans provide a unified roadmap that enhances coordination, accountability, and responsiveness across the Division's diverse activities.",
  "The Annual Blood Collection Plan remains the cornerstone, setting evidence-based targets for each Regional Centre for Blood Transfusion (RCBT) according to projected demand from Treatment Health Facilities. Complementary plans reinforce this foundation: The Annual Blood Collection Plan emphasizes on blood donation activities, the Hemovigilance Plan ensures safety and monitoring, the Bio-Banking Plan advances cornea retrieval and supply for patients in need and the Field Activity Plan strengthens outreach and operational delivery. Each plan plays a distinct role, yet all converge toward the shared mission of saving lives and sustaining Rwanda's healthcare system.",
  "The BTD Annual Activity Book is therefore more than a compilation of tasks; it is a strategic instrument of preparedness and excellence. By uniting diverse activities under one vision, it reflects Rwanda's commitment to proactive planning, innovation, and resilience in healthcare.",
];

const DEFAULT_SIGNATORIES = [
  { name: "Prof. MUVUNYI Mambo Claude", role: "Director General",                        signatureUrl: null },
  { name: "Dr Isabelle MUKAGATARE",     role: "Head of Biomedical Services\nDepartment", signatureUrl: null },
  { name: "Dr Thomas MUYOMBO",          role: "Blood Transfusion\nDivision Manager",     signatureUrl: null },
];

const DEFAULT_SUMMARY_ROWS = [
  { month:"JANUARY",   kigali:[56,2619],  butare:[34,1302],  karongi:[33,1161],  ruhengeri:[43,1677],  rwamagana:[35,1302],  rcbtTotal:[201,8061],  rmth:[14,800],   rnp:[2,440],  grand:[217,9301]  },
  { month:"FEBRUARY",  kigali:[56,2619],  butare:[35,1363],  karongi:[32,1423],  ruhengeri:[42,1513],  rwamagana:[34,1300],  rcbtTotal:[199,8218],  rmth:[18,1571],  rnp:[3,240],  grand:[220,10029] },
  { month:"MARCH",     kigali:[51,2645],  butare:[29,1250],  karongi:[34,1349],  ruhengeri:[39,1463],  rwamagana:[36,1326],  rcbtTotal:[189,8033],  rmth:[20,1190],  rnp:[4,335],  grand:[213,9558]  },
  { month:"APRIL",     kigali:[45,2138],  butare:[34,1292],  karongi:[31,1141],  ruhengeri:[43,1607],  rwamagana:[34,1154],  rcbtTotal:[187,7332],  rmth:[14,800],   rnp:[4,410],  grand:[205,8542]  },
  { month:"MAY",       kigali:[64,3026],  butare:[35,1363],  karongi:[35,1509],  ruhengeri:[43,1616],  rwamagana:[38,1456],  rcbtTotal:[215,8970],  rmth:[18,1571],  rnp:[2,180],  grand:[235,10721] },
  { month:"JUNE",      kigali:[42,2344],  butare:[33,1381],  karongi:[34,1206],  ruhengeri:[39,1469],  rwamagana:[36,1339],  rcbtTotal:[184,7739],  rmth:[20,1190],  rnp:[4,335],  grand:[208,9264]  },
  { month:"JULY",      kigali:[39,2028],  butare:[29,1107],  karongi:[30,1176],  ruhengeri:[36,1348],  rwamagana:[39,1379],  rcbtTotal:[173,7038],  rmth:[14,800],   rnp:[4,560],  grand:[191,8398]  },
  { month:"AUGUST",    kigali:[40,2027],  butare:[33,1254],  karongi:[31,1012],  ruhengeri:[37,1439],  rwamagana:[33,1216],  rcbtTotal:[174,6948],  rmth:[18,1571],  rnp:[3,240],  grand:[195,8759]  },
  { month:"SEPTEMBER", kigali:[45,2353],  butare:[30,1214],  karongi:[30,1229],  ruhengeri:[38,1406],  rwamagana:[38,1342],  rcbtTotal:[181,7544],  rmth:[20,1190],  rnp:[3,275],  grand:[204,9009]  },
  { month:"OCTOBER",   kigali:[58,2752],  butare:[35,1371],  karongi:[34,1256],  ruhengeri:[43,1780],  rwamagana:[37,1457],  rcbtTotal:[207,8616],  rmth:[14,800],   rnp:[4,610],  grand:[225,10026] },
  { month:"NOVEMBER",  kigali:[59,2626],  butare:[39,1391],  karongi:[32,1384],  ruhengeri:[44,1684],  rwamagana:[36,1284],  rcbtTotal:[210,8369],  rmth:[18,1571],  rnp:[3,240],  grand:[231,10180] },
  { month:"DECEMBER",  kigali:[38,2322],  butare:[30,1325],  karongi:[32,1329],  ruhengeri:[42,1660],  rwamagana:[38,1380],  rcbtTotal:[180,8016],  rmth:[20,1205],  rnp:[3,275],  grand:[203,9496]  },
];

const DEFAULT_SUMMARY_TOTAL = {
  kigali:[593,29499], butare:[396,15613], karongi:[388,15175], ruhengeri:[489,18662],
  rwamagana:[434,15935], rcbtTotal:[2300,94884], rmth:[208,14259], rnp:[39,4140], grand:[2547,113283],
};

export const DEFAULT_MONTHLY_PLAN = {
  JANUARY: [
    { label:"WEEK 1", k:[5,5,201],   b:[2,2,61],   ko:[1,1,30],  ru:[4,4,146],  rw:[2,2,60],  t:[14,498],  rmth:[0,0,0],   rnp:[0,0,0]   },
    { label:"WEEK 2", k:[11,11,533], b:[7,7,308],  ko:[7,7,213], ru:[8,8,298],  rw:[8,8,290], t:[41,1642], rmth:[1,1,150], rnp:[0,0,0]   },
    { label:"WEEK 3", k:[14,14,578], b:[6,6,226],  ko:[8,8,316], ru:[11,11,458],rw:[8,8,272], t:[47,1850], rmth:[2,6,350], rnp:[1,1,350] },
    { label:"WEEK 4", k:[13,13,570], b:[10,10,387],ko:[8,8,269], ru:[10,10,376],rw:[8,8,263], t:[49,1865], rmth:[3,6,265], rnp:[0,0,0]   },
    { label:"WEEK 5", k:[11,11,437], b:[8,8,280],  ko:[8,8,293], ru:[9,9,349],  rw:[8,8,377], t:[44,1736], rmth:[1,1,35],  rnp:[1,1,90]  },
    { label:"FIXED",  k:[1,1,200],   b:[1,1,40],   ko:[1,1,40],  ru:[1,1,50],   rw:[1,1,40],  t:[5,370], bus:[1,100], rmth:[0,0,0], rnp:[0,0,0] },
  ],
  FEBRUARY: [
    { label:"WEEK 1", k:[2,2,60],    b:[0,0,0],    ko:[0,0,0],   ru:[0,0,0],    rw:[1,1,36],  t:[3,96],    rmth:[0,0,0],   rnp:[0,0,0]   },
    { label:"WEEK 2", k:[16,16,589], b:[9,9,343],  ko:[7,7,324], ru:[11,11,418],rw:[8,8,316], t:[51,1990], rmth:[3,6,456], rnp:[1,1,100] },
    { label:"WEEK 3", k:[14,14,675], b:[8,8,287],  ko:[7,7,247], ru:[10,10,350],rw:[11,11,384],t:[50,1943],rmth:[5,7,560], rnp:[0,0,0]   },
    { label:"WEEK 4", k:[12,12,472], b:[7,7,264],  ko:[9,9,534], ru:[10,10,347],rw:[7,7,263], t:[45,1880], rmth:[2,4,330], rnp:[2,2,140] },
    { label:"WEEK 5", k:[10,10,523], b:[10,10,429],ko:[8,8,278], ru:[10,10,348],rw:[6,6,261], t:[44,1839], rmth:[1,1,225], rnp:[0,0,0]   },
    { label:"FIXED",  k:[1,1,200],   b:[1,1,40],   ko:[1,1,40],  ru:[1,1,50],   rw:[1,1,40],  t:[5,370], bus:[1,100], rmth:[0,0,0], rnp:[0,0,0] },
  ],
  MARCH: [
    { label:"WEEK 1", k:[1,1,55],    b:[0,0,0],    ko:[0,0,0],   ru:[0,0,0],    rw:[1,1,30],  t:[2,85],    rmth:[0,0,0],   rnp:[0,0,0]   },
    { label:"WEEK 2", k:[10,10,408], b:[7,7,250],  ko:[8,8,339], ru:[9,9,320],  rw:[7,7,263], t:[41,1580], rmth:[1,1,200], rnp:[0,0,0]   },
    { label:"WEEK 3", k:[14,14,678], b:[7,7,358],  ko:[8,8,231], ru:[8,8,299],  rw:[8,8,300], t:[45,1866], rmth:[2,5,340], rnp:[1,1,150] },
    { label:"WEEK 4", k:[13,13,631], b:[6,6,295],  ko:[6,6,345], ru:[10,10,378],rw:[9,9,311], t:[44,1960], rmth:[1,3,105], rnp:[2,2,125] },
    { label:"WEEK 5", k:[10,10,543], b:[6,6,245],  ko:[7,7,218], ru:[9,9,332],  rw:[7,7,258], t:[39,1596], rmth:[1,3,90],  rnp:[1,1,60]  },
    { label:"WEEK 6", k:[1,1,30],    b:[2,2,62],   ko:[4,4,176], ru:[2,2,74],   rw:[3,3,124], t:[12,466],  rmth:[2,6,381], rnp:[0,0,0]   },
    { label:"FIXED",  k:[1,1,200],   b:[1,1,40],   ko:[1,1,40],  ru:[1,1,60],   rw:[1,1,40],  t:[5,380], bus:[1,100], rmth:[1,2,74], rnp:[0,0,0] },
  ],
  APRIL: [
    { label:"WEEK 1", k:[5,5,174],   b:[4,4,150],  ko:[5,5,200], ru:[7,7,245],  rw:[4,4,143], t:[25,912],  rmth:[0,0,0],   rnp:[1,1,60]  },
    { label:"WEEK 2", k:[10,10,440], b:[6,6,209],  ko:[6,6,210], ru:[9,9,310],  rw:[7,7,248], t:[38,1417], rmth:[1,4,120], rnp:[0,0,0]   },
    { label:"WEEK 3", k:[9,9,300],   b:[7,7,315],  ko:[7,7,234], ru:[11,11,461],rw:[8,8,259], t:[42,1569], rmth:[2,6,350], rnp:[0,0,0]   },
    { label:"WEEK 4", k:[10,10,454], b:[10,10,377],ko:[7,7,248], ru:[9,9,331],  rw:[8,8,252], t:[44,1662], rmth:[2,2,260], rnp:[3,3,350] },
    { label:"WEEK 5", k:[9,9,370],   b:[6,6,201],  ko:[5,5,209], ru:[6,6,210],  rw:[6,6,212], t:[32,1202], rmth:[1,2,70],  rnp:[0,0,0]   },
    { label:"FIXED",  k:[1,1,200],   b:[1,1,40],   ko:[1,1,40],  ru:[1,1,50],   rw:[1,1,40],  t:[5,370], bus:[1,100], rmth:[0,0,0], rnp:[0,0,0] },
  ],
  MAY: [
    { label:"WEEK 1", k:[2,2,55],    b:[1,1,41],   ko:[1,1,25],  ru:[3,3,98],   rw:[1,1,30],  t:[8,249],   rmth:[1,2,86],  rnp:[0,0,0]   },
    { label:"WEEK 2", k:[14,14,589], b:[9,9,333],  ko:[8,8,325], ru:[8,8,301],  rw:[8,8,375], t:[47,1923], rmth:[2,4,210], rnp:[2,2,180] },
    { label:"WEEK 3", k:[18,18,690], b:[8,8,287],  ko:[8,8,313], ru:[9,9,328],  rw:[8,8,326], t:[51,1944], rmth:[4,6,680], rnp:[0,0,0]   },
    { label:"WEEK 4", k:[17,17,597], b:[7,7,267],  ko:[8,8,339], ru:[12,12,500],rw:[11,11,351],t:[55,2054],rmth:[3,5,370], rnp:[0,0,0]   },
    { label:"WEEK 5", k:[11,11,695], b:[9,9,395],  ko:[9,9,467], ru:[10,10,339],rw:[9,9,334], t:[48,2230], rmth:[1,1,225], rnp:[0,0,0]   },
    { label:"FIXED",  k:[1,1,200],   b:[1,1,40],   ko:[1,1,40],  ru:[1,1,50],   rw:[1,1,40],  t:[5,370], bus:[1,100], rmth:[0,0,0], rnp:[0,0,0] },
  ],
  JUNE: [
    { label:"WEEK 1", k:[10,10,488], b:[10,10,362],ko:[7,7,241], ru:[8,8,298],  rw:[7,7,267], t:[42,1656], rmth:[3,6,540], rnp:[0,0,0]   },
    { label:"WEEK 2", k:[13,13,573], b:[7,7,342],  ko:[8,8,261], ru:[9,9,327],  rw:[8,8,338], t:[45,1841], rmth:[1,5,185], rnp:[2,2,125] },
    { label:"WEEK 3", k:[9,9,467],   b:[6,6,295],  ko:[8,8,233], ru:[10,10,379],rw:[9,9,321], t:[42,1695], rmth:[2,6,360], rnp:[1,1,150] },
    { label:"WEEK 4", k:[6,6,373],   b:[7,7,280],  ko:[8,8,371], ru:[8,8,302],  rw:[8,8,283], t:[37,1609], rmth:[1,3,105], rnp:[1,1,60]  },
    { label:"WEEK 5", k:[2,2,143],   b:[2,2,62],   ko:[2,2,60],  ru:[3,3,113],  rw:[3,3,90],  t:[12,468],  rmth:[0,0,0],   rnp:[0,0,0]   },
    { label:"FIXED",  k:[1,1,200],   b:[1,1,40],   ko:[1,1,40],  ru:[1,1,50],   rw:[1,1,40],  t:[5,370], bus:[1,100], rmth:[0,0,0], rnp:[0,0,0] },
  ],
  JULY: [
    { label:"WEEK 1", k:[4,4,162],   b:[4,4,138],  ko:[4,4,128], ru:[5,5,196],  rw:[3,3,131], t:[20,755],  rmth:[0,0,0],   rnp:[1,1,60]  },
    { label:"WEEK 2", k:[11,11,497], b:[6,6,283],  ko:[9,9,376], ru:[10,10,369],rw:[9,9,320], t:[45,1845], rmth:[2,6,420], rnp:[0,0,0]   },
    { label:"WEEK 3", k:[8,8,317],   b:[7,7,255],  ko:[6,6,192], ru:[9,9,324],  rw:[8,8,273], t:[38,1361], rmth:[1,5,200], rnp:[2,2,410] },
    { label:"WEEK 4", k:[9,9,320],   b:[6,6,231],  ko:[6,6,288], ru:[7,7,251],  rw:[10,10,344],t:[38,1434],rmth:[1,1,110], rnp:[1,1,90]  },
    { label:"WEEK 5", k:[5,5,296],   b:[5,5,160],  ko:[4,4,152], ru:[4,4,148],  rw:[8,8,271], t:[26,1027], rmth:[1,2,70],  rnp:[0,0,0]   },
    { label:"FIXED",  k:[1,1,200],   b:[1,1,40],   ko:[1,1,40],  ru:[1,1,50],   rw:[1,1,40],  t:[5,370], bus:[1,100], rmth:[0,0,0], rnp:[0,0,0] },
  ],
  AUGUST: [
    { label:"WEEK 1", k:[2,2,60],    b:[1,1,35],   ko:[1,1,30],  ru:[0,0,0],    rw:[1,1,30],  t:[5,155],   rmth:[1,1,50],  rnp:[0,0,0]   },
    { label:"WEEK 2", k:[9,9,325],   b:[9,9,306],  ko:[7,7,219], ru:[10,10,419],rw:[7,7,267], t:[42,1536], rmth:[2,4,163], rnp:[0,0,0]   },
    { label:"WEEK 3", k:[8,8,378],   b:[6,6,198],  ko:[6,6,155], ru:[5,5,209],  rw:[9,9,335], t:[34,1275], rmth:[4,5,683], rnp:[1,1,100] },
    { label:"WEEK 4", k:[9,9,386],   b:[7,7,333],  ko:[7,7,218], ru:[9,9,300],  rw:[8,8,291], t:[40,1528], rmth:[2,6,225], rnp:[2,2,140] },
    { label:"WEEK 5", k:[9,9,504],   b:[8,8,310],  ko:[8,8,320], ru:[11,11,414],rw:[7,7,253], t:[43,1801], rmth:[1,1,225], rnp:[0,0,0]   },
    { label:"WEEK 6", k:[1,1,74],    b:[1,1,32],   ko:[1,1,30],  ru:[1,1,37],   rw:[4,4,173], t:[8,346],   rmth:[1,1,225], rnp:[0,0,0]   },
    { label:"FIXED",  k:[1,1,200],   b:[1,1,40],   ko:[1,1,40],  ru:[1,1,60],   rw:[1,1,40],  t:[5,380], bus:[1,100], rmth:[0,0,0], rnp:[0,0,0] },
  ],
  SEPTEMBER: [
    { label:"WEEK 1", k:[6,6,355],   b:[5,5,159],  ko:[5,5,219], ru:[8,8,296],  rw:[7,7,251], t:[31,1280], rmth:[2,3,450], rnp:[0,0,0]   },
    { label:"WEEK 2", k:[9,9,395],   b:[6,6,282],  ko:[5,5,284], ru:[8,8,298],  rw:[8,8,290], t:[36,1549], rmth:[2,7,245], rnp:[2,2,125] },
    { label:"WEEK 3", k:[12,12,475], b:[6,6,230],  ko:[9,9,371], ru:[8,8,331],  rw:[8,8,259], t:[43,1666], rmth:[3,7,405], rnp:[1,1,150] },
    { label:"WEEK 4", k:[12,12,557], b:[7,7,306],  ko:[6,6,200], ru:[8,8,267],  rw:[9,9,315], t:[42,1645], rmth:[1,3,90],  rnp:[0,0,0]   },
    { label:"WEEK 5", k:[4,4,171],   b:[5,5,197],  ko:[4,4,115], ru:[5,5,164],  rw:[5,5,187], t:[23,834],  rmth:[0,0,0],   rnp:[0,0,0]   },
    { label:"FIXED",  k:[1,1,200],   b:[1,1,40],   ko:[1,1,40],  ru:[1,1,60],   rw:[1,1,40],  t:[5,380], bus:[1,100], rmth:[0,0,0], rnp:[0,0,0] },
  ],
  OCTOBER: [
    { label:"WEEK 1", k:[8,8,365],   b:[4,4,153],  ko:[4,4,142], ru:[5,5,209],  rw:[5,5,196], t:[26,1065], rmth:[1,1,150], rnp:[1,1,60]  },
    { label:"WEEK 2", k:[13,13,586], b:[8,8,326],  ko:[7,7,275], ru:[11,11,439],rw:[8,8,335], t:[47,1961], rmth:[3,5,300], rnp:[0,0,0]   },
    { label:"WEEK 3", k:[12,12,405], b:[8,8,263],  ko:[8,8,274], ru:[9,9,361],  rw:[8,8,334], t:[45,1637], rmth:[3,6,290], rnp:[2,2,460] },
    { label:"WEEK 4", k:[13,13,525], b:[6,6,248],  ko:[8,8,293], ru:[9,9,395],  rw:[10,10,343],t:[46,1804],rmth:[1,2,60],  rnp:[1,1,90]  },
    { label:"WEEK 5", k:[10,10,471], b:[8,8,341],  ko:[6,6,232], ru:[8,8,316],  rw:[5,5,209], t:[37,1569], rmth:[0,0,0],   rnp:[0,0,0]   },
    { label:"FIXED",  k:[1,1,200],   b:[1,1,40],   ko:[1,1,40],  ru:[1,1,60],   rw:[1,1,40],  t:[5,380], bus:[1,100], rmth:[0,0,0], rnp:[0,0,0] },
  ],
  NOVEMBER: [
    { label:"WEEK 1", k:[1,1,30],    b:[1,1,31],   ko:[0,0,0],   ru:[0,0,0],    rw:[1,1,69],  t:[3,130],   rmth:[0,0,0],   rnp:[0,0,0]   },
    { label:"WEEK 2", k:[13,13,560], b:[8,8,320],  ko:[7,7,327], ru:[11,11,460],rw:[8,8,317], t:[47,1984], rmth:[3,6,456], rnp:[1,1,100] },
    { label:"WEEK 3", k:[14,14,562], b:[10,10,334],ko:[7,7,400], ru:[10,10,367],rw:[8,8,281], t:[49,1944], rmth:[5,7,560], rnp:[0,0,0]   },
    { label:"WEEK 4", k:[13,13,563], b:[9,9,298],  ko:[7,7,245], ru:[10,10,351],rw:[9,9,297], t:[48,1754], rmth:[1,3,105], rnp:[2,2,140] },
    { label:"WEEK 5", k:[15,15,581], b:[8,8,301],  ko:[10,10,372],ru:[11,11,408],rw:[9,9,280],t:[53,1942], rmth:[1,1,225], rnp:[0,0,0]   },
    { label:"WEEK 6", k:[1,1,30],    b:[2,2,67],   ko:[0,0,0],   ru:[1,1,38],   rw:[0,0,0],   t:[4,135],   rmth:[1,1,225], rnp:[0,0,0]   },
    { label:"FIXED",  k:[1,1,200],   b:[1,1,40],   ko:[1,1,40],  ru:[1,1,60],   rw:[1,1,40],  t:[5,380], bus:[1,100], rmth:[0,0,0], rnp:[0,0,0] },
  ],
  DECEMBER: [
    { label:"WEEK 1", k:[7,7,388],   b:[7,7,354],  ko:[6,6,196], ru:[8,8,314],  rw:[8,8,303], t:[36,1555], rmth:[3,6,540], rnp:[0,0,0]   },
    { label:"WEEK 2", k:[8,8,435],   b:[7,7,267],  ko:[8,8,351], ru:[10,10,370],rw:[8,8,300], t:[41,1723], rmth:[1,5,220], rnp:[2,2,125] },
    { label:"WEEK 3", k:[8,8,416],   b:[6,6,307],  ko:[7,7,327], ru:[11,11,437],rw:[8,8,257], t:[40,1744], rmth:[2,6,355], rnp:[0,0,0]   },
    { label:"WEEK 4", k:[8,8,393],   b:[5,5,220],  ko:[5,5,231], ru:[6,6,241],  rw:[8,8,330], t:[32,1415], rmth:[1,3,90],  rnp:[1,1,150] },
    { label:"WEEK 5", k:[5,5,290],   b:[4,4,137],  ko:[5,5,184], ru:[6,6,238],  rw:[5,5,150], t:[25,999],  rmth:[0,0,0],   rnp:[0,0,0]   },
    { label:"FIXED",  k:[1,1,200],   b:[1,1,40],   ko:[1,1,40],  ru:[1,1,60],   rw:[1,1,40],  t:[5,380], bus:[1,100], rmth:[0,0,0], rnp:[0,0,0] },
  ],
};

// ─── RDF (RMTH BB) and RNP default sections ──────────────────────────────────
export const DEFAULT_RDF_SECTIONS = [
  {
    months: ["JANUARY","APRIL","JULY","OCTOBER"],
    label: "ESTIMATED SESS. / UNITS 1",
    sites: [
      { name: "BMTC Nasho",                         rows: [["11-12/01/2026",2,300],["19-20/04/2026",2,300],["07-08/07/2026",2,300],["04-05/10/2026",2,300]] },
      { name: "Gatsibo - Nyagatare",                rows: [["13-17/01/2026",5,200],["14-18/04/2026",5,200],["14-18/07/2026",5,200],["10-14/10/2026",5,200]] },
      { name: "Kimihurura",                          rows: [["20/01/2026",1,110],   ["23/04/2026",1,110],   ["21/07/2026",1,110],   ["15/10/2026",1,110]]   },
      { name: "Busanza - Nyanza",                   rows: [["25-26/01/2026",2,70],  ["28-29/04/2026",2,70], ["29-30/07/2026",2,70], ["07-08/10/2026",2,70]] },
      { name: "Ngoma - Kirehe",                     rows: [["21-24/01/2026",4,120], ["09-13/04/2026",4,120],["09-12/07/2026",4,120],["17-20/10/2026",4,120]] },
    ],
    estimated: [[14,800],[14,800],[14,800],[14,800]], totalRow: [56,3200],
  },
  {
    months: ["FEBRUARY","MAY","AUGUST","NOVEMBER"],
    label: "ESTIMATED SESS. / UNITS 2",
    sites: [
      { name: "Rubavu - Mukamira",                  rows: [["06-07/02/2026",2,86], ["2-3/05/2026",2,86],   ["09-10/08/2026",2,86],  ["6-7/11/2026",2,86]]   },
      { name: "Musanze - Nyakinama",                rows: [["3-5/02/2026",3,120],  ["5-7/05/2026",3,120],  ["04-06/08/2026",3,120], ["03-05/11/2026",3,120]] },
      { name: "Bigogwe",                             rows: [["08-09/02/2026",2,500],["12-13/05/2026",2,500],["12-13/08/2026",2,500], ["8-9/11/2026",2,500]]  },
      { name: "WB Rubavu",                          rows: [["10/02/2026",1,90],    ["04/05/2026",1,90],    ["14/08/2026",1,90],     ["10/11/2026",1,90]]    },
      { name: "WB Karongi",                         rows: [["11/02/2026",1,50],    ["14/05/2026",1,50],    ["01/08/2026",1,50],     ["11/11/2026",1,50]]    },
      { name: "WB Nyamasheke",                      rows: [["12/02/2026",1,50],    ["15/05/2026",1,50],    ["16/08/2026",1,50],     ["12/11/2026",1,50]]    },
      { name: "Rutsiro - Karongi - Nyamasheke",     rows: [["13-15/02/2026",3,120],["16-18/05/2026",3,120],["17-19/08/2026",3,120], ["13-15/11/2026",3,120]] },
      { name: "Rusizi",                              rows: [["16-18/02/2026",3,105],["19-21/05/2026",3,105],["21-23/08/2026",3,105], ["20-22/11/2026",3,105]] },
      { name: "CTC Gabiro",                         rows: [["22-23/02/2026",2,450],["24-25/05/2026",2,450],["30-31/08/2026",2,450], ["29-30/11/2026",2,450]] },
    ],
    estimated: [[18,1571],[18,1571],[18,1571],[18,1571]], totalRow: [72,6284],
  },
  {
    months: ["MARCH","JUNE","SEPTEMBER","DECEMBER"],
    label: "ESTIMATED SESS. / UNITS 3",
    sites: [
      { name: "Nyaruguru - Nyamagabe",              rows: [["27-31/03/2026",5,185],["09-13/06/2026",5,185],["07-11/09/2026",5,185], ["8-12/12/2026",5,220]]  },
      { name: "RMA Gako",                           rows: [["01/03/2026",1,200],   ["07/06/2026",1,200],   ["06/09/2026",1,200],    ["06/12/2026",1,200]]    },
      { name: "Burera - Gicumbi",                   rows: [["03-05/03/2026",3,90], ["02-04/06/2026",3,90], ["12-14/09/2026",3,90],  ["01-03/12/2026",3,90]]  },
      { name: "Kanombe Barracks",                   rows: [["06-07/03/2026",2,250],["05-06/06/2026",2,250],["04-05/09/2026",2,250], ["04-05/12/2026",2,250]]  },
      { name: "Huye - Gisagara",                    rows: [["11-13/03/2026",3,105],["23-25/06/2026",3,105],["15-17/09/2026",3,105], ["15-17/12/2026",3,105]]  },
      { name: "Kamonyi - Muhanga",                  rows: [["17-19/03/2026",3,90], ["16-18/06/2026",3,90], ["22-24/09/2026",3,90],  ["21-23/12/2026",3,90]]  },
      { name: "Kigali (Kami, Rebero, Mont Kigali)", rows: [["24-26/03/2026",3,270],["19-21/06/2026",3,270],["18-20/09/2026",3,270], ["18-20/12/2026",3,250]]  },
    ],
    estimated: [[20,1190],[20,1190],[20,1190],[20,1205]], totalRow: [80,4775],
  },
];

export const DEFAULT_RNP_SECTIONS = [
  {
    months: ["JANUARY","APRIL","JULY","OCTOBER"],
    label: "ESTIMATED UNITS 1",
    sites: [
      { name: "PTS Gishari",                rows: [["18/01/2026",1,350],["26/04/2026",1,200],["19/07/2026",1,350],["18/10/2026",1,400]] },
      { name: "EASTERN region",             rows: [null,               ["25/04/2026",1,60], ["18/07/2026",1,60], ["17/10/2026",1,60]]  },
      { name: "Interforce Kimironko (SIF)", rows: [["27/01/2026",1,90], ["22/04/2026",1,90], ["22/07/2026",1,90], ["22/10/2026",1,90]]  },
      { name: "Northern Region",            rows: [null,               ["02/04/2026",1,60], ["02/07/2026",1,60], ["02/10/2026",1,60]]  },
    ],
    estimated: [[2,440],[4,410],[4,560],[4,610]], totalRow: [14,2020],
  },
  {
    months: ["FEBRUARY","MAY","AUGUST","NOVEMBER"],
    label: "ESTIMATED UNITS 2",
    sites: [
      { name: "Headquarter Kacyiru", rows: [["19/02/2026",1,80], ["09/05/2026",1,80], ["23/08/2026",1,80], ["18/11/2026",1,80]]  },
      { name: "CTTC Mayange",        rows: [["08/02/2026",1,100],["10/05/2026",1,100],["16/08/2026",1,100],["02/11/2026",1,100]] },
      { name: "Central Region",      rows: [["20/02/2026",1,60], null,                ["22/08/2026",1,60], ["19/11/2026",1,60]]  },
    ],
    estimated: [[3,240],[2,180],[3,240],[3,240]], totalRow: [11,900],
  },
  {
    months: ["MARCH","JUNE","SEPTEMBER","DECEMBER"],
    label: "ESTIMATED UNITS 3",
    sites: [
      { name: "Muhima Traffic Police", rows: [["15/03/2026",1,65], ["14/06/2026",1,65], ["13/09/2026",1,65], ["13/12/2026",1,65]]  },
      { name: "NPC Musanze",           rows: [["08/03/2026",1,150],["15/06/2026",1,150],["14/09/2026",1,150],["27/12/2026",1,150]] },
      { name: "SOUTHERN Region",       rows: [["14/03/2026",1,60], ["08/06/2026",1,60], null,               ["07/12/2026",1,60]]  },
      { name: "Western region",        rows: [["20/03/2026",1,60], ["27/06/2026",1,60], ["12/09/2026",1,60], null]                },
    ],
    estimated: [[4,335],[4,335],[3,275],[3,275]], totalRow: [14,1220],
  },
];

export const getDefaultConfig = (year) => ({
  cover: {
    line1: "RWANDA BIOMEDICAL CENTRE",
    line2: "BIOMEDICAL SERVICES DEPARTMENT",
    line3: "BLOOD TRANSFUSION DIVISION",
    bookTitle: "Activity\nBook",
  },
  contents: {
    items: [
      `A. ANNUAL BLOOD COLLECTION PLAN ${year}`,
      `B. CORNEA RETRIEVAL PLAN ${year}`,
      `C. HEMOVIGILANCE AND CORNEA-VIGILANCE ACTIVITY PLAN ${year}`,
      `D. FIELD ACTIVITY PLAN ${year}`,
    ],
    copyrightText: "This plan is the Blood Transfusion Division property, under the Biomedical Services Department of the Rwanda Biomedical Centre.",
  },
  foreword: {
    paragraphs: DEFAULT_FOREWORD_PARAGRAPHS,
    approvedDate: `............/............/${year}`,
    signatories: DEFAULT_SIGNATORIES,
  },
  toc: {
    entries: [
      { title: "FOREWORD",                                                  page: 2   },
      { title: "TABLE OF CONTENT",                                          page: 3   },
      { title: `A. ANNUAL BLOOD COLLECTION PLAN ${year}`,                   page: 4   },
      { title: `B. CORNEA RETRIEVAL PLAN ${year}`,                          page: 54  },
      { title: `C. HEMOVIGILANCE AND CORNIAL-VIGILANCE ACTIVITY PLAN ${year}`, page: 58 },
      { title: `D. FIELD ACTIVITY PLAN ${year}`,                            page: 101 },
      { title: "CONTRIBUTORS",                                              page: 107 },
    ],
  },
  divider: {
    sectionTitle: `A. ANNUAL BLOOD COLLECTION PLAN ${year}`,
  },
  summary: {
    rows:  DEFAULT_SUMMARY_ROWS,
    total: DEFAULT_SUMMARY_TOTAL,
  },
  monthlyPlan: DEFAULT_MONTHLY_PLAN,
  dailyPlan: {},
  rdf: {
    title: "RWANDA DEFENCE FORCE ANNUAL BLOOD COLLECTION PLAN",
    sections: DEFAULT_RDF_SECTIONS,
  },
  rnp: {
    title: "RWANDA NATIONAL POLICE ANNUAL BLOOD COLLECTION PLAN",
    unitsLabel: "Est.UNITS",
    sections: DEFAULT_RNP_SECTIONS,
  },
});

// ─── Deep merge — arrays replaced wholesale, objects merged recursively ────────
function deepMerge(defaults, overrides) {
  const result = { ...defaults };
  for (const key of Object.keys(overrides ?? {})) {
    if (
      key in defaults &&
      typeof defaults[key] === "object" &&
      !Array.isArray(defaults[key]) &&
      defaults[key] !== null
    ) {
      result[key] = deepMerge(defaults[key], overrides[key]);
    } else {
      result[key] = overrides[key];
    }
  }
  return result;
}

// ─── Public API (sync — reads localStorage cache, falls back to defaults) ─────
export function resolveConfig(year) {
  try {
    const raw = localStorage.getItem(STORAGE_KEY(year));
    if (!raw) return getDefaultConfig(year);
    return deepMerge(getDefaultConfig(year), JSON.parse(raw));
  } catch {
    return getDefaultConfig(year);
  }
}

export function saveConfig(year, config) {
  try {
    localStorage.setItem(STORAGE_KEY(year), JSON.stringify(config));
    return true;
  } catch {
    return false;
  }
}

export function resetConfig(year) {
  try {
    localStorage.removeItem(STORAGE_KEY(year));
    return true;
  } catch {
    return false;
  }
}

// ─── Async API (persists to DB; keeps localStorage as fast-read cache) ────────
import("../../../utils/queries/abcpConfigQuery").catch(() => null); // warm module

export async function loadConfigAsync(year) {
  try {
    const { fetchAbcpConfig } = await import("../../../utils/queries/abcpConfigQuery");
    const serverConfig = await fetchAbcpConfig(year);
    if (serverConfig && typeof serverConfig === "object") {
      // Refresh the localStorage cache so resolveConfig is consistent
      localStorage.setItem(STORAGE_KEY(year), JSON.stringify(serverConfig));
      return deepMerge(getDefaultConfig(year), serverConfig);
    }
  } catch {}
  return resolveConfig(year);
}

export async function persistConfig(year, config) {
  try {
    const { saveAbcpConfig } = await import("../../../utils/queries/abcpConfigQuery");
    await saveAbcpConfig(year, config);
    localStorage.setItem(STORAGE_KEY(year), JSON.stringify(config));
    return true;
  } catch {
    // Fall back to localStorage-only on API failure
    return saveConfig(year, config);
  }
}

export async function clearConfig(year) {
  try {
    const { resetAbcpConfig } = await import("../../../utils/queries/abcpConfigQuery");
    await resetAbcpConfig(year);
    localStorage.removeItem(STORAGE_KEY(year));
    return true;
  } catch {
    return resetConfig(year);
  }
}
